import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { OrgRatingChip } from "@/components/OrgRatingChip";
import {
  ArrowRight, Compass, Users, Award, Trophy, MapPin, Clock, Quote, Laptop, Route, Sprout, CalendarHeart, Mic, Newspaper, MessageCircle, HandHeart,
} from "lucide-react";

const TYPE_ICON = { interview: MessageCircle, podcast: Mic, article: Newspaper };

const AWARD_TIER_CARDS = [
  { label: "Bronze Certificate", sub: "5 verified hours" },
  { label: "Silver Certificate", sub: "25 verified hours" },
  { label: "Gold Certificate", sub: "100 verified hours" },
];

function SectionHead({ kicker, title, to, linkText }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">{kicker}</div>
        <h2 className="font-display text-3xl sm:text-4xl text-forest mt-3 leading-tight tracking-tight">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="text-sm font-medium text-forest hover:text-forest-hover inline-flex items-center gap-1.5">
          {linkText} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

export default function Explore() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [featuredOpps, setFeaturedOpps] = useState([]);
  const [matched, setMatched] = useState([]);
  const [stories, setStories] = useState([]);
  const [content, setContent] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [days, setDays] = useState([]);
  const [orgRatings, setOrgRatings] = useState({});
  const stripRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, scroll: 0, moved: false });

  useEffect(() => {
    api.exploreCounts().then(setCounts).catch(() => {});
    api.featuredOpps(4).then(setFeaturedOpps).catch(() => {});
    api.stories(2).then(setStories).catch(() => {});
    api.contentList(3).then(setContent).catch(() => {});
    api.experiences(3).then(setExperiences).catch(() => {});
    api.awarenessDays(5).then(setDays).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.user_type === "individual") api.matchedForMe().then(setMatched).catch(() => {});
  }, [user]);

  const personalised = matched.length > 0;
  const curated = featuredOpps.filter(f => f.featured);
  const handpicked = personalised
    ? [...curated, ...matched.filter(m => !curated.some(c => c.id === m.id)),
       ...featuredOpps.filter(f => !f.featured && !matched.some(m => m.id === f.id))].slice(0, 4)
    : featuredOpps;

  // Phase 8: org rating chips on the handpicked cards.
  useEffect(() => {
    const ids = [...new Set([...featuredOpps, ...matched].map(o => o.org_user_id).filter(Boolean))];
    if (ids.length === 0) return;
    api.orgRatings(ids)
      .then((d) => setOrgRatings(prev => ({ ...prev, ...d.ratings })))
      .catch(() => {});
  }, [featuredOpps, matched]);

  const tiles = [
    { icon: Compass, label: "Volunteering", count: counts?.opportunities, unit: "open roles", to: "/opportunities" },
    { icon: Users, label: "Community", count: counts?.stories, unit: "voices", to: "/community" },
    { icon: Award, label: "Awards", count: counts?.awards, unit: "to earn", to: "/recognition?tab=awards" },
    { icon: Trophy, label: "Leaderboard", count: counts?.volunteers, unit: "volunteers", to: "/recognition?tab=leaderboard" },
    { icon: Laptop, label: "Remote roles", count: counts?.remote_roles, unit: "work anywhere", to: "/opportunities?remote=1" },
    { icon: Route, label: "Ways to help", count: 6, unit: "ways in", to: "/pathways" },
  ];

  return (
    <div className="bg-cream" data-testid="explore-page">
      <Seo title="Ways to take part" description="Opportunities, community voices, awards and pathways — your quick way into everything on CivicHearth." />

      {/* Hero + tiles */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="animate-fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Orientation</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-forest mt-4 leading-[1.05]">
            Ways to <span className="shine-text">take part.</span>
          </h1>
          <p className="text-forest/70 mt-5 text-lg max-w-xl">Opportunities, community voices, awards and pathways — all in one place.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-10">
          {tiles.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.06}>
              <Link to={t.to} data-testid={`explore-tile-${t.label.toLowerCase().replace(" ", "-")}`}
                className="block bg-white border border-forest/10 rounded-2xl p-5 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <t.icon className="w-6 h-6 text-terracotta" />
                <div className="font-display text-lg text-forest mt-3">{t.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{t.count ?? "—"} {t.unit}</div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Awareness days strip */}
      {days.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8" data-testid="awareness-strip">
          <Reveal>
            <div className="civic-glass rounded-3xl p-6 sm:p-7">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">
                <CalendarHeart className="w-4 h-4 text-terracotta" /> Coming up
              </div>
              <div
                ref={stripRef}
                data-testid="explore-awareness-strip"
                className="flex gap-3 mt-4 overflow-x-auto pb-1 no-scrollbar cursor-grab active:cursor-grabbing select-none"
                onMouseDown={(e) => { drag.current = { down: true, startX: e.pageX, scroll: stripRef.current.scrollLeft, moved: false }; }}
                onMouseMove={(e) => {
                  if (!drag.current.down) return;
                  const dx = e.pageX - drag.current.startX;
                  if (Math.abs(dx) > 3) drag.current.moved = true;
                  stripRef.current.scrollLeft = drag.current.scroll - dx;
                }}
                onMouseUp={() => { drag.current.down = false; }}
                onMouseLeave={() => { drag.current.down = false; }}
              >
                {days.map((d) => (
                  <div key={d.name} data-testid={`awareness-day-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="shrink-0 bg-white border border-forest/10 rounded-2xl px-5 py-4 min-w-[190px]">
                    <div className="font-display text-forest leading-snug">{d.name}</div>
                    <div className="text-xs text-neutral-500 mt-1.5">
                      {new Date(d.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                      {" · "}
                      <span className="text-terracotta font-medium">{d.days_until === 0 ? "today" : `in ${d.days_until} days`}</span>
                    </div>
                    <Badge variant="outline" className="rounded-full text-[10px] capitalize border-forest/20 text-forest mt-2">{d.tag}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* Featured opportunities */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <Reveal><SectionHead kicker={personalised ? "Handpicked for you" : "Handpicked"} title={personalised ? "Matched to your values & skills" : "Featured opportunities"} to="/opportunities" linkText="Browse all" /></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {handpicked.map((o, i) => (
            <Reveal key={o.id} delay={i * 0.08}>
              <Link to={`/opportunities/${o.id}`} data-testid={`explore-opp-${o.id}`}
                className="block bg-white border border-forest/10 rounded-2xl overflow-hidden h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <div className="h-28 bg-forest/10 relative">
                  {o.cover_image ? (
                    <img src={imgSrc(o.cover_image)} alt={o.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-forest to-forest/70" />
                  )}
                  {o.community_led && (
                    <Badge data-testid={`explore-community-led-badge-${o.id}`} className="absolute top-2 left-2 rounded-full text-[10px] bg-terracotta text-white border-none"><Sprout className="w-3 h-3 mr-1" />Community-led</Badge>
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-terracotta/80 font-bold inline-flex items-center gap-2">
                    {o.org_name}
                    <OrgRatingChip rating={orgRatings[o.org_user_id]} testId={`explore-rating-chip-${o.id}`} />
                  </div>
                  <div className="font-display text-lg text-forest mt-1 leading-snug line-clamp-2">{o.title}</div>
                  <div className="flex flex-wrap gap-2.5 mt-3 text-xs text-neutral-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{o.remote ? "Remote" : o.location}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{o.hours_required}h</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Community */}
      <section className="max-w-7xl mx-auto px-6 py-14" data-testid="explore-community-section">
        <Reveal><SectionHead kicker="Community" title="Voices, stories & spotlights" to="/community" linkText="Visit the community hub" /></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          {/* Spotlight content */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {content.slice(0, 2).map((c, i) => {
              const Icon = TYPE_ICON[c.content_type] || Newspaper;
              return (
                <Reveal key={c.id} delay={i * 0.08}>
                  <Link to={`/community/${c.id}`} data-testid={`explore-content-${c.id}`}
                    className="group relative block rounded-2xl overflow-hidden h-full min-h-[280px] border border-forest/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                    {c.cover_image ? (
                      <img src={imgSrc(c.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="absolute inset-0 bg-forest" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent" />
                    <div className="relative flex flex-col justify-end h-full p-6 text-white">
                      <Badge className="w-fit rounded-full text-[10px] uppercase tracking-wide bg-white/15 backdrop-blur text-white border-none">
                        <Icon className="w-3 h-3 mr-1" />{c.content_type}
                      </Badge>
                      <div className="font-display text-xl leading-snug mt-3">{c.title}</div>
                      <div className="text-xs text-white/70 mt-2">{c.author_name}</div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          {/* Stories + experiences mini feed */}
          <Reveal delay={0.15}>
            <div className="space-y-4">
              {stories.map((s) => (
                <Link key={s.id} to="/stories" className="block bg-white border border-forest/10 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                  <Quote className="w-4 h-4 text-terracotta/60" />
                  <p className="text-sm text-neutral-700 leading-relaxed line-clamp-2 mt-2">"{s.text}"</p>
                  <div className="text-[11px] text-neutral-500 mt-2">{s.user_name} · {s.hours}h with {s.org_name}</div>
                </Link>
              ))}
              {experiences.slice(0, 2).map((e) => (
                <div key={e.id} className="bg-white border border-forest/10 rounded-2xl p-5">
                  <div className="flex items-center gap-2.5">
                    <Avatar className="w-7 h-7">
                      <AvatarImage src={imgSrc(e.picture)} alt={e.name} />
                      <AvatarFallback className="bg-forest text-white text-[10px]">{e.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="text-[11px] text-neutral-500">{e.name} · {e.hours}h verified · {e.org_name}</div>
                  </div>
                  <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mt-2">"{e.narrative}"</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Awards preview */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <Reveal><SectionHead kicker="Recognise" title="Awards & badges" to="/recognition?tab=awards" linkText="See awards" /></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
          {AWARD_TIER_CARDS.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08}>
              <Link to="/awards" className="block bg-white border border-forest/10 rounded-2xl p-6 text-center h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <Award className={`w-10 h-10 mx-auto ${i === 0 ? "text-amber-700" : i === 1 ? "text-neutral-400" : "text-amber-500"}`} />
                <div className="font-display text-lg text-forest mt-3">{c.label}</div>
                <div className="text-xs text-neutral-500 mt-1">{c.sub}</div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Grassroots + Ask-a-neighbour CTAs (equal weight, Phase 10) */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-forest text-white p-8 sm:p-10 h-full flex flex-col">
              <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-terracotta/20 blur-2xl" />
              <div className="relative flex-1">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-terracotta font-bold">
                  <Sprout className="w-4 h-4" /> Grassroots
                </div>
                <h2 className="font-display text-3xl leading-tight tracking-tight mt-3">Your street. Your park. Your event.</h2>
                <p className="text-white/80 mt-3">Anyone can host a community event — a litter pick, a park tidy-up, a street library. Neighbours join in one click, and every hour still counts.</p>
              </div>
              <div className="relative flex flex-wrap gap-3 mt-6">
                <Link to="/post-opportunity"><Button data-testid="explore-host-event-btn" className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">Host a community event</Button></Link>
                <Link to="/opportunities"><Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12">Join one near you</Button></Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="relative overflow-hidden rounded-3xl bg-terracotta text-white p-8 sm:p-10 h-full flex flex-col">
              <div className="pointer-events-none absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-forest/25 blur-2xl" />
              <div className="relative flex-1">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/90 font-bold">
                  <HandHeart className="w-4 h-4" /> Neighbour to neighbour
                </div>
                <h2 className="font-display text-3xl leading-tight tracking-tight mt-3">Need a hand? Just ask.</h2>
                <p className="text-white/85 mt-3">A lift to the clinic, help carrying the shopping, someone to fix a shelf — post a help request and neighbours nearby can offer in one click.</p>
              </div>
              <div className="relative flex flex-wrap gap-3 mt-6">
                <Link to="/help-request/new"><Button data-testid="explore-ask-help-btn" className="rounded-full bg-white text-terracotta hover:bg-white/90 px-7 h-12 font-semibold">Ask a neighbour for help</Button></Link>
                <Link to="/opportunities?type=help"><Button data-testid="explore-browse-help-btn" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12">Browse help requests</Button></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-14 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight tracking-tight">Ready to make a difference?</h2>
          <p className="text-forest/70 mt-3 max-w-lg mx-auto">Build your skills, earn recognition, and create positive change in your community.</p>
          <Link to={user ? "/discover" : "/login"} data-testid="explore-start-cta"><Button className="rounded-full bg-forest hover:bg-forest-hover px-7 h-12 mt-7">Start volunteering</Button></Link>
        </Reveal>
      </section>
    </div>
  );
}
